import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerClose,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@libs/zapi-ui/shadcn";
import { Typography, AppButton } from "@libs/zapi-ui/custom";
import { XIcon, DownloadSimple, Eye, DotsThree } from "@phosphor-icons/react";
import { formatTimeAgo } from "@/zapi-frontend/utils/dates";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { useExportHistory } from "../hooks/useExportHistory";

type ExportedFilesDrawerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
};

export function ExportedFilesDrawer({ open, onOpenChange, projectId }: ExportedFilesDrawerProps) {
  const { data: exportHistory, isLoading } = useExportHistory(projectId, 50);

  const handlePreview = (fileUrl: string) => {
    window.open(fileUrl, "_blank");
  };

  const handleDownload = async (fileUrl: string, fileName: string) => {
    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange} direction="right">
      <DrawerContent className="max-w-[520px] ml-auto h-full flex flex-col">
        <DrawerHeader className="border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <DrawerTitle className="font-medium">Exported Files</DrawerTitle>
            <DrawerClose asChild>
              <AppButton appearance="text" size="icon" className="size-6 p-0">
                <XIcon className="size-5" />
              </AppButton>
            </DrawerClose>
          </div>
        </DrawerHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {isLoading ? (
            <div className="h-full flex items-center justify-center">
              <Typography variant="body3" color="zinc500">
                Loading exports...
              </Typography>
            </div>
          ) : !exportHistory?.exports.length ? (
            <div className="h-full flex flex-col justify-center items-center text-center">
              <Typography variant="body3" className="text-innoveraZinc-500">
                No exports yet. Use the Export button to generate reports.
              </Typography>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {exportHistory.exports.map((exp) => (
                <div
                  key={exp.id}
                  className={cn(
                    "bg-white py-3 px-4 flex items-center justify-between border border-innoveraZinc-150 rounded-lg hover:bg-innoveraZinc-50 transition-colors"
                  )}
                >
                  <div className="flex flex-col gap-1 flex-1 min-w-0">
                    <Typography variant="label2" color="zinc900" className="text-[14px] leading-[20px] truncate">
                      {exp.fileName}
                    </Typography>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Typography variant="body3" color="zinc600" className="text-[12px] leading-[18px]">
                        {formatTimeAgo(exp.createdAt)}
                      </Typography>
                      <Typography variant="body3" color="zinc500" className="text-[12px] leading-[18px]">
                        • {exp.user.firstName} {exp.user.lastName}
                      </Typography>
                      {exp.fileSize && (
                        <Typography variant="body3" color="zinc500" className="text-[12px] leading-[18px]">
                          • {(exp.fileSize / 1024).toFixed(0)} KB
                        </Typography>
                      )}
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <AppButton appearance="text" size="icon" className="size-6 p-0 ml-3 flex-shrink-0">
                        <DotsThree className="size-6" color="var(--innovera-zinc900)" />
                      </AppButton>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-[160px]">
                      <DropdownMenuItem
                        onClick={() => handlePreview(exp.fileUrl)}
                        className="flex items-center gap-2 cursor-pointer"
                      >
                        <Eye className="size-4" weight="regular" />
                        <Typography variant="label2">Preview</Typography>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleDownload(exp.fileUrl, exp.fileName)}
                        className="flex items-center gap-2 cursor-pointer"
                      >
                        <DownloadSimple className="size-4" weight="regular" />
                        <Typography variant="label2">Download</Typography>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ))}
            </div>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
